import {
  useState,
  useRef,
  useEffect,
  Fragment,
  ForwardRefRenderFunction,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { Divider, Grid, Stack, Typography } from "@mui/material";
import { debounce } from "lodash";

// material ui icons
import DeleteRounded from "@mui/icons-material/DeleteRounded";

// project import
import Selector from "components/select/selector";
import { useTranslation } from "utils/locales/utilityFunctions";
import { beautifyNumber, strInputNumberCorrection } from "utils/numberModification";
import TextFieldInput from "components/input/textField";
import SmallIconButton from "components/buttons/smallIconButton";
import { FirestoreInventoryItemProps } from "types/auth";
import useAuth from "hooks/useAuth";
import { generateUUID } from "utils/deviceTools";
import useConfig from "hooks/useConfig";
import { getLogVolumesFromWeightedDiameter } from "@foresting-tomorrow/forest_tools_inventory";
import { RawWoodOptionProps } from "./options";
import AutoCompleteFiltered from "components/input/autoCompleteFiltered";

type ErrorCode =
  | "error-custom-id-already-in-use"
  | "error-custom-id-not-available"
  | "error-checking-custom-id-availability"
  | "error-reserving-custom-id"
  | "error-releasing-old-id"
  | "error-no-id-returned";

type HandleCustomIdParams = {
  customId: number;
  prevId: number | null;
  index: number;
  rowId: string;
  rows: LogEntry[];
};

export type LogEntry = {
  id: string;
  uniqueMeasurementId: number | null;
  uniqueIdForRelease: number | null; // Used for releasing the ID later
  error: ErrorCode | null;
  loading: boolean;
  species: string;
  length: string;
  diameter: string;
  diameter2?: string;
  volume: string;
  qClass: string;
};

const NUMERIC_FIELDS = new Set<keyof LogEntry>(["length", "diameter", "diameter2"]);

export type RawWoodInputMeasurementLogsHandler = {
  getCurrentLogRows: () => LogEntry[];
  getLogRows: () => Promise<LogEntry[]>;
};

type RawWoodInputMeasurementLogsProps = {
  variant: FirestoreInventoryItemProps["inventoryType"];
  disableInputs: boolean;
  buyerApprovedDisable: boolean;
  assortment: string;
  logClassOptions: RawWoodOptionProps[];
  logClassExportOptions: RawWoodOptionProps[];
  initRows?: LogEntry[];
  speciesOptions: any[];
  doubleDiameter?: boolean;
  woodLogAssortment?: string;
};

const RawWoodInputMeasurementLogs: ForwardRefRenderFunction<
  RawWoodInputMeasurementLogsHandler,
  RawWoodInputMeasurementLogsProps
> = (
  {
    disableInputs,
    buyerApprovedDisable,
    initRows,
    assortment,
    logClassOptions,
    logClassExportOptions,
    speciesOptions,
    doubleDiameter = false,
    woodLogAssortment,
  },
  ref
) => {
  // --- Hooks --- //
  const { userOrg, callCloudFunction } = useAuth();
  const { translate } = useTranslation();
  const { i18n } = useConfig();

  // --- States --- //
  const [logRows, setLogRows] = useState<LogEntry[]>([]);
  const [uniquePointIdError, setUniquePointIdError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const latestRowsRef = useRef(logRows);

  useEffect(() => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, [logRows.length]);

  useEffect(() => {
    latestRowsRef.current = logRows;
  }, [logRows]);

  useEffect(() => {
    if (initRows && initRows.length > 0) {
      // Run through all existing rows and recalculate volume to account for change in assortment
      const updatedRows = initRows.map((row) => {
        const newVol = calculateVolume(row.length, row.diameter, row.diameter2, row.species);
        return { ...row, volume: newVol };
      });
      const rowId = "new-" + generateUUID();
      const newRow: LogEntry = {
        id: rowId,
        uniqueMeasurementId: null,
        uniqueIdForRelease: null,
        species: updatedRows[0].species || "",
        length: "",
        diameter: "",
        diameter2: "",
        volume: "",
        qClass: updatedRows[0].qClass || "",
        loading: true,
        error: null,
      };
      const extendedRows = [...updatedRows, newRow];
      insertNextAvailableId(rowId, extendedRows);
      setLogRows(extendedRows);
    } else {
      const rowId = "new-" + generateUUID();
      const updated: LogEntry[] = [
        {
          id: rowId,
          uniqueMeasurementId: null,
          uniqueIdForRelease: null,
          species: "",
          length: "",
          diameter: "",
          diameter2: "",
          volume: "",
          qClass: "",
          loading: true,
          error: null,
        },
      ];
      insertNextAvailableId(rowId, updated);
      setLogRows([...updated]);
    }
  }, [initRows]);

  useImperativeHandle(ref, () => ({
    // Inspect unsaved edits without releasing reserved IDs or discarding rows.
    getCurrentLogRows: () => logRows,
    getLogRows: async () => {
      return await filterAndReleaseUnusedRows();
    },
  }));

  const debouncedCheckUniquePointId = useMemo(() => {
    return debounce(
      (customId: number, oldUniqueId: number | null, index: number, rowId: string) => {
        // read fresh rows from ref to avoid stale closures
        const rows = latestRowsRef.current;
        handleCustomIdChange({ customId, prevId: oldUniqueId, index, rowId, rows });
      },
      500
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount to avoid state updates after unmount
  useEffect(() => {
    return () => debouncedCheckUniquePointId.cancel();
  }, [debouncedCheckUniquePointId]);

  const calculateVolume = (
    length: string,
    diameter: string,
    diameter2?: string,
    species?: string
  ) => {
    if (!length || !diameter) return "";
    // Convert to numbers, replacing comma with dot for float parsing
    const l = parseFloat(length.replace(",", "."));
    const d1 = parseFloat(diameter.replace(",", "."));
    const d2 = diameter2 ? parseFloat(diameter2.replace(",", ".")) : NaN;
    if (isNaN(l) || isNaN(d1) || l === 0 || d1 === 0) return "";
    const d2c = !isNaN(d2) ? d2 : d1;
    const avgDiameter = (d1 + d2c) / 2;
    const volumes = getLogVolumesFromWeightedDiameter({
      logLengthM: l,
      logMidtDiameterCm: avgDiameter,
      species,
    });
    if (assortment === "longLogs") return beautifyNumber(volumes.handMeasuredOverbarkVolume, 2);
    return beautifyNumber(volumes.overbarkVolume, 2);
  };

  const handleChange = async (index: number, field: keyof LogEntry, rawValue: string) => {
    // Apply correction only if it's a numeric field
    const value = NUMERIC_FIELDS.has(field)
      ? strInputNumberCorrection(rawValue, i18n, 2) // <-- pass your i18n
      : rawValue;

    setLogRows((prev) => {
      const next = prev.map((row, i) => {
        if (i !== index) return row;
        // Update the specific field and recalculate volume
        const nextRow = { ...row, [field]: value } as LogEntry;
        const vol = calculateVolume(
          nextRow.length,
          nextRow.diameter,
          nextRow.diameter2,
          nextRow.species
        );
        return { ...nextRow, volume: vol };
      });
      // if last row is now fully filled, append a new one (loading=true)
      const isLast = index === next.length - 1;
      if (isLast && isFullyFilled(next[index])) {
        const rowId = "new-" + generateUUID();
        next.push({
          id: rowId,
          uniqueMeasurementId: null,
          uniqueIdForRelease: null,
          loading: true,
          species: next[index].species,
          qClass: next[index].qClass,
          length: "",
          diameter: "",
          diameter2: "",
          volume: "",
          error: null,
        });
        // reserve id for the new row (async, but we already pushed the skeleton)
        insertNextAvailableId(rowId, next);
      }
      return next;
    });
  };

  // Handle change uniqueMeasurementId
  const handleChangeUniqueMeasurementId = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
    rowId: string
  ) => {
    const value = event.target.value;
    const regex = /^[0-9\b]+$/;
    const notAcceptedChars = ["e", "-", "+", ".", ","];
    // @ts-ignore
    if (notAcceptedChars.includes(event?.nativeEvent?.data)) return;

    if (value === "") {
      updateRow(index, (row) => ({
        uniqueMeasurementId: null,
        uniqueIdForRelease: null,
        error: null,
      }));
      return;
    }
    if (!regex.test(value)) return;
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    // Capture the old value from *current* state
    const oldUniqueId = logRows[index]?.uniqueMeasurementId ?? null;
    // Mark uniqueIdForRelease if we haven't yet and the value actually changes
    updateRow(index, (row) => ({
      uniqueMeasurementId: parsed,
      uniqueIdForRelease:
        row.uniqueIdForRelease ??
        (row.uniqueMeasurementId !== null ? row.uniqueMeasurementId : null),
    }));

    // Debounced backend sync
    debouncedCheckUniquePointId(parsed, oldUniqueId, index, rowId);
  };

  async function handleDeleteRow(index: number) {
    if (!userOrg?.id) return;
    const row = logRows[index];
    if (!row) return;
    // mark row busy
    setLoading(index, true);
    clearError(index);
    // collect ids to release (avoid duplicates)
    const toRelease = new Set<number>();
    if (row.uniqueMeasurementId != null) toRelease.add(row.uniqueMeasurementId);
    if (row.uniqueIdForRelease != null) toRelease.add(row.uniqueIdForRelease);
    try {
      // release all ids associated with this row
      for (const id of toRelease) {
        await apiReleaseId(userOrg.id, id);
      }
    } catch (e) {
      console.error("Failed to release id(s) on delete:", e);
      setError(index, "error-releasing-old-id");
      setUniquePointIdError("error-releasing-old-id");
      setLoading(index, false);
      return; // do not remove the row if release failed
    }
    // remove the row
    setLogRows((prev) => prev.filter((_, i) => i !== index));
    // ensure we always have at least one row present
    // and pre-reserve an ID for it
    setLogRows((prev) => {
      if (prev.length > 0) return prev;
      const rowId = generateUUID();
      const next: LogEntry[] = [
        {
          id: rowId,
          uniqueMeasurementId: null,
          uniqueIdForRelease: null,
          species: "",
          length: "",
          diameter: "",
          diameter2: "",
          volume: "",
          qClass: "",
          loading: true,
          error: null,
        },
      ];
      // kick off reservation for the new first row
      insertNextAvailableId(rowId, next);
      return next;
    });
  }

  const filterAndReleaseUnusedRows = async (): Promise<LogEntry[]> => {
    if (!userOrg?.id) return logRows;
    const validRows: LogEntry[] = [];
    const unusedRows: LogEntry[] = [];
    for (const row of logRows) {
      const hasSpecies = row.species.trim() !== "";
      const hasVolume = row.volume.trim() !== "" && row.volume !== "0";
      if (hasSpecies && hasVolume) {
        validRows.push(row);
      } else {
        unusedRows.push(row);
      }
    }
    // release IDs for unused rows
    for (const row of unusedRows) {
      const toRelease = new Set<number>();
      if (row.uniqueMeasurementId != null) toRelease.add(row.uniqueMeasurementId);
      if (row.uniqueIdForRelease != null) toRelease.add(row.uniqueIdForRelease);
      for (const id of toRelease) {
        try {
          await apiReleaseId(userOrg.id, id);
        } catch (e) {
          console.error(`Failed to release ID ${id} for row ${row.id}:`, e);
        }
      }
    }
    // Update state to exclude unused rows
    if (unusedRows.length > 0) {
      setLogRows(validRows);
    }
    return validRows;
  };

  // --- Functionality for handling unique measurement IDs --- //
  const insertNextAvailableId = async (rowId: string, rows: LogEntry[]) => {
    if (!userOrg?.id) return;
    const index = rows.findIndex((r) => r.id === rowId);
    if (index === -1) return;

    setLoading(index, true);
    clearError(index);

    try {
      const id = await apiReserveNextId(userOrg.id);
      if (!id) {
        setError(index, "error-no-id-returned");
        return;
      }
      applyNewId(index, id);
    } catch (e) {
      console.error("Error getting next available ID:", e);
      setError(index, "error-no-id-returned");
      console.error("Failed to reserve next available ID:", e);
      updateRow(index, () => ({ uniqueMeasurementId: null }));
    } finally {
      setLoading(index, false);
    }
  };

  async function handleCustomIdChange({
    customId,
    prevId,
    index,
    rowId,
    rows,
  }: HandleCustomIdParams) {
    if (!userOrg?.id) return;
    if (customId === prevId) return;

    setLoading(index, true);
    clearError(index);

    let newIdReserved = false;

    try {
      // Local duplicate → revert & bail
      if (hasLocalDuplicate(rows, customId, rowId)) {
        setError(index, "error-custom-id-already-in-use");
        revertToPreviousId(index, prevId); // <-- revert
        return;
      }

      // Backend availability → revert & bail if not available
      const available = await apiCheckAvailable(userOrg.id, customId);
      if (!available) {
        setError(index, "error-custom-id-not-available");
        // revertToPreviousId(index, prevId); // <-- revert
        return;
      }

      // Reserve new id first
      const reserved = await apiReserveId(userOrg.id, customId);
      if (!reserved) {
        setError(index, "error-reserving-custom-id");
        revertToPreviousId(index, prevId); // <-- revert
        return;
      }
      newIdReserved = true; // <-- track reservation

      // Release old if present
      const currentRow = rows[index];
      if (currentRow?.uniqueIdForRelease) {
        try {
          await apiReleaseId(userOrg.id, currentRow.uniqueIdForRelease);
        } catch (e) {
          // Roll back the newly reserved id to avoid leaks
          try {
            await apiReleaseId(userOrg.id, customId); // <-- rollback
          } catch (_) {
            // swallow secondary failure; we’re already erroring
          }
          setError(index, "error-releasing-old-id");
          setUniquePointIdError("error-releasing-old-id");
          revertToPreviousId(index, prevId); // <-- revert
          return;
        }
      }

      // Success: apply new id & clear release marker
      applyNewId(index, customId);
    } catch (e) {
      // Generic failure: roll back if we reserved
      if (newIdReserved) {
        try {
          await apiReleaseId(userOrg.id, customId); // <-- rollback
        } catch (_) {}
      }
      setError(index, "error-checking-custom-id-availability");
      setUniquePointIdError("error-checking-custom-id-availability");
      revertToPreviousId(index, prevId); // <-- revert
    } finally {
      setLoading(index, false);
    }
  }

  // --- Helper functions --- //
  const setLoading = (index: number, loading: boolean) => updateRow(index, () => ({ loading }));

  const clearError = (index: number) => updateRow(index, () => ({ error: null }));

  const setError = (index: number, error: ErrorCode) => updateRow(index, () => ({ error }));

  const applyNewId = (index: number, customId: number) =>
    updateRow(index, () => ({
      uniqueMeasurementId: customId,
      uniqueIdForRelease: null,
      error: null,
    }));

  const revertToPreviousId = (index: number, prevId: number | null) =>
    updateRow(index, () => ({
      uniqueMeasurementId: prevId,
      uniqueIdForRelease: null,
    }));

  const isFullyFilled = (log: LogEntry) => {
    return Object.entries(log).every(([key, value]) => {
      if (key === "loading" || key === "error" || key === "uniqueIdForRelease" || key === "id")
        return true;
      if (key === "uniqueMeasurementId") return value !== null; // numeric/null check
      if (key === "diameter2" && !doubleDiameter) return true;
      if (typeof value === "string") return value.trim() !== "";
      return true;
    });
  };

  function hasLocalDuplicate(rows: LogEntry[], customId: number, currentRowId: string) {
    return rows.some((r) => r.uniqueMeasurementId === customId && r.id !== currentRowId);
  }

  function updateRow(index: number, updater: (row: LogEntry) => Partial<LogEntry>) {
    setLogRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...updater(row) } : row)));
  }

  // API wrappers
  async function apiReserveNextId(orgId: string) {
    const res = await callCloudFunction("reserveNextId", { orgId, group: "WoodLog" });
    return res?.data?.id as number | undefined;
  }

  async function apiCheckAvailable(orgId: string, id: number) {
    const res = await callCloudFunction("checkCustomIdAvailable", { orgId, id, group: "WoodLog" });
    return !!res?.data?.available;
  }

  async function apiReserveId(orgId: string, id: number) {
    const res = await callCloudFunction("reserveCustomId", { orgId, id, group: "WoodLog" });
    return !!res?.data?.id;
  }

  async function apiReleaseId(orgId: string, id: number) {
    await callCloudFunction("releaseReservedId", { orgId, id, group: "WoodLog" });
  }

  // --- Render --- //
  let firstGridW = 2.4;
  if (doubleDiameter) firstGridW = 2.2;
  const lastGridW = 1.5;
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5">{translate("raw-wood-input-measurement-logs-header")}</Typography>
      </Grid>

      {logRows.map((log, i) => (
        <Fragment key={`log-${log.id}`}>
          <Grid item xs={12} sm={firstGridW}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <SmallIconButton
                tooltipText={translate("raw-wood-input-measurement-logs-remove-tooltip")}
                status="enabled"
                onClick={() => handleDeleteRow(i)}
                orientation="horizontal"
                disabled={!isFullyFilled(log) || logRows.length === 1 || disableInputs || buyerApprovedDisable}
              >
                <DeleteRounded fontSize="small" color={"primary"} />
              </SmallIconButton>

              <Divider orientation="vertical" flexItem />

              <TextFieldInput
                title={translate("raw-wood-input-measurement-logs-id")}
                value={log.uniqueMeasurementId ? log.uniqueMeasurementId.toString() : ""}
                handleChange={(e) => handleChangeUniqueMeasurementId(e, i, log.id)}
                fullWidth
                showLabel
                disabled={disableInputs || buyerApprovedDisable}
                dontDisableOnShowSpinner={true}
                error={log.error !== null}
                loading={log.loading}
              />
            </Stack>
          </Grid>

          <Grid item xs={6} sm={(12 - firstGridW - lastGridW) / (doubleDiameter ? 5 : 4)}>
            <AutoCompleteFiltered
              options={speciesOptions}
              value={
                speciesOptions.find((opt) => opt.id === log.species && log.species !== "non") ||
                null
              }
              changeHandler={(_, v) => handleChange(i, "species", v ? v.id : "non")}
              disabled={disableInputs || buyerApprovedDisable}
              numberOfOptions={5}
              label={translate("raw-wood-input-measurement-logs-species")}
              placeholder={translate("species-search")}
            />
          </Grid>

          <Grid item xs={6} sm={(12 - firstGridW - lastGridW) / (doubleDiameter ? 5 : 4)}>
            <TextFieldInput
              title={translate("raw-wood-input-measurement-logs-length")}
              value={log.length}
              handleChange={(e) => handleChange(i, "length", e.target.value)}
              fullWidth
              showLabel
              inputAdornment="m"
              disabled={disableInputs || buyerApprovedDisable}
            />
          </Grid>

          <Grid item xs={6} sm={(12 - firstGridW - lastGridW) / (doubleDiameter ? 5 : 4)}>
            <TextFieldInput
              title={translate("raw-wood-input-measurement-logs-diameter")}
              value={log.diameter}
              handleChange={(e) => handleChange(i, "diameter", e.target.value)}
              fullWidth
              showLabel
              inputAdornment="cm"
              disabled={disableInputs || buyerApprovedDisable}
            />
          </Grid>

          {doubleDiameter && (
            <Grid item xs={6} sm={(12 - firstGridW - lastGridW) / (doubleDiameter ? 5 : 4)}>
              <TextFieldInput
                title={translate("raw-wood-input-measurement-logs-diameter")}
                value={log.diameter2 || ""}
                handleChange={(e) => handleChange(i, "diameter2", e.target.value)}
                fullWidth
                showLabel
                inputAdornment="cm"
                disabled={disableInputs || buyerApprovedDisable}
              />
            </Grid>
          )}

          <Grid item xs={6} sm={(12 - firstGridW - lastGridW) / (doubleDiameter ? 5 : 4)}>
            <TextFieldInput
              title={translate("raw-wood-input-measurement-logs-volume")}
              value={log.volume}
              handleChange={() => {}}
              fullWidth
              showLabel
              disabled
              inputAdornment="m3"
            />
          </Grid>

          <Grid item xs={6} sm={lastGridW}>
            <Selector
              title={translate("raw-wood-input-measurement-logs-class")}
              value={log.qClass}
              options={
                woodLogAssortment === "exportSawlogs" ? logClassExportOptions : logClassOptions
              }
              label
              fullWidth
              error={false}
              disabled={disableInputs || buyerApprovedDisable}
              changeHandler={(e) => handleChange(i, "qClass", e.target.value)}
            />
          </Grid>

          {logRows.length - 1 > i && (
            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>
          )}

          {logRows.length - 1 === i && <div ref={bottomRef} />}
        </Fragment>
      ))}
    </>
  );
};

export default forwardRef(RawWoodInputMeasurementLogs);
